import { Link } from "react-router";
import { ChevronRight, Loader2 } from "lucide-react";
import { STATUS_META } from "@/components/prosthesis/prosthesis-meta";
import { trpc } from "@/lib/trpc";
import { reais } from "@/lib/dinheiro";
import { cn } from "@/lib/cn";

const data = (d: Date | string | null) => (d ? new Date(d).toLocaleDateString("pt-BR") : "—");

function Numero({ rotulo, valor, cor }: { rotulo: string; valor: string; cor?: string }) {
  return (
    <div className="min-w-0 rounded-xl border border-zinc-200 bg-white p-4 shadow-sm dark:border-border dark:bg-card">
      <p className="truncate text-xs font-medium text-zinc-500 dark:text-muted-foreground">{rotulo}</p>
      <p className={cn("mt-1 truncate text-xl font-bold tabular-nums text-zinc-900 dark:text-foreground", cor)}>{valor}</p>
    </div>
  );
}

/** O movimento do cliente com o laboratório: o mês em números e os pedidos mais recentes. */
export function VisaoGeralDoCadastro({ id }: { id: string }) {
  const consulta = trpc.partner.resumo.useQuery({ id });
  const r = consulta.data;
  if (consulta.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
      </div>
    );
  }
  if (!r) return null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Numero rotulo="Pedidos no mês" valor={String(r.pedidosNoMes)} />
        <Numero rotulo="Em produção agora" valor={String(r.emProducao)} />
        <Numero rotulo="Entregues no mês" valor={String(r.entreguesNoMes)} />
        {r.faturadoNoMesCents != null ? <Numero rotulo="Faturado no mês" valor={reais(r.faturadoNoMesCents)} /> : <Numero rotulo="Último pedido" valor={data(r.ultimoPedidoEm)} />}
        {r.financeiro && (
          <>
            <Numero rotulo="Em aberto nos extratos" valor={reais(r.financeiro.emAbertoCents)} />
            <Numero rotulo="Vencido" valor={reais(r.financeiro.vencidoCents)} cor={r.financeiro.vencidoCents ? "text-red-600" : undefined} />
          </>
        )}
      </div>

      <section className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-border dark:bg-card">
        <header className="flex items-center justify-between border-b border-zinc-100 bg-zinc-50/60 px-4 py-2.5 dark:border-border dark:bg-muted/30">
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">Pedidos recentes</h3>
          {r.ultimoPedidoEm && <span className="text-xs text-zinc-500 dark:text-muted-foreground">Último em {data(r.ultimoPedidoEm)}</span>}
        </header>
        {r.recentes.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-zinc-500 dark:text-muted-foreground">Nenhum pedido deste cliente ainda.</p>
        ) : (
          <ul className="divide-y divide-zinc-100 dark:divide-border/50">
            {r.recentes.map((p) => {
              const meta = STATUS_META[p.status as keyof typeof STATUS_META];
              return (
                <li key={p.id}>
                  <Link to={`/lab/pedidos?abrir=${p.id}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 hover:bg-zinc-50/60 dark:hover:bg-muted/40">
                    <span className="hidden w-14 text-xs font-semibold tabular-nums text-zinc-500 sm:inline">{p.numero != null ? `Nº ${p.numero}` : ""}</span>
                    <span className="w-full min-w-0 sm:w-auto sm:flex-1">
                      <span className="block truncate text-sm font-semibold text-zinc-900 dark:text-foreground">{p.paciente}</span>
                      <span className="block truncate text-[11px] text-zinc-500 dark:text-muted-foreground">
                        {p.servicos.join(", ")} · pedido em {data(p.criadoEm)}
                        {p.prazo ? ` · prazo ${data(p.prazo)}` : ""}
                      </span>
                    </span>
                    {meta && (
                      <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ backgroundColor: meta.bg, color: meta.color }}>
                        {meta.label}
                      </span>
                    )}
                    <span className="ml-auto w-24 text-right text-sm font-semibold tabular-nums text-zinc-900 dark:text-foreground">{p.valorCents != null ? reais(p.valorCents) : "—"}</span>
                    <ChevronRight className="h-4 w-4 text-zinc-400" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
